import React, { useEffect } from "react";
import {
  DEFAULT_AMOUNT_SAMPLE,
  DEFAULT_LABORATORY,
} from "../../../utils/constants";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import { ProcedureType, UserType } from "@app/types/genericTables.types";
import {
  AnalysisCurrent,
  PreparationTypes,
  SampleStateType,
} from "@app/types/samples.types";
import { useEquipment, useTypesUnits } from "@services/servicesGenericsTables";
import useTabs from "@app/pages/samples/utils/useTab";
import { useSample } from "@app/pages/samples/utils/useSample";
import useAuth from "@app/context/AuthContext";
import useAnalysis from "@app/pages/samples/utils/useAnalysis";
import useFilteredProcedures from "@app/pages/samples/hook/useFilteredProcedures";
import { formatDateOrNow } from "@app/utils/formatDate";
import { OPTIONS_A_B, OPTIONS_YES_NO } from "../iodo131/utils/constants";
import { setsAnalysisPhasesDP } from "@app/pages/samples/config/phases/AnalysisPhases";

// Código del tipo de unidad por defecto para Dosimetría Ambiental (mSv/Año)
const DEFAULT_UNIT_TYPE_CODE = "5";

const getDefaultValues = (
  currentPreparationIndex?: PreparationTypes,
  currentCounting?: AnalysisCurrent,
  currentCalculation?: AnalysisCurrent,
  sampling?: SampleStateType,
  user?: UserType,
  filteredProceduresPRE?: ProcedureType,
  filteredProceduresCON?: ProcedureType,
  filteredCalculationCAL?: ProcedureType,
  idMuestra?: string
) => {
  const currentSampling = sampling?.current;
  const currentCountingIndex =
    currentPreparationIndex?.countings?.[currentCounting?.index as number];
  const currentCalculationIndex =
    currentCountingIndex?.calculations[currentCalculation?.index as number];

  const currentSample = useSample.getState().get(Number(idMuestra));

  const consultEquipment = (id: number) => {
    return useEquipment
      .getState()
      .equipmentList.find((equipment) => equipment.id === id);
  };

  const processingCurrent =
    useSample.getState().stateSample?.processing?.current || [];

  const dryingProcessing =
    processingCurrent && processingCurrent.length > 0
      ? processingCurrent.find((item) => item.processing_category?.id === 3)
      : undefined;

  // Obtener el tipo de unidad por defecto (mSv/Año) si no existe uno previo
  const getDefaultUnitType = () => {
    if (currentCountingIndex?.unit_types) {
      return currentCountingIndex.unit_types;
    }
    const typesUnitsList = useTypesUnits.getState().typesUnitsis;
    return typesUnitsList.find((unit) => unit.codigo === DEFAULT_UNIT_TYPE_CODE);
  };

  return {
    analysis_analyst: currentPreparationIndex?.analyst || user,
    preparation_procedure:
      currentPreparationIndex?.preparation_procedure ||
      filteredProceduresPRE ||
      undefined,
    start_date: formatDateOrNow(currentPreparationIndex?.start_date),
    analysis_amount_sample: currentPreparationIndex?.amount_sample || 0,
    analysis_equipment: currentPreparationIndex?.equipment || [],

    //counting
    counting_analyst: currentCountingIndex?.analyst || user,
    counting_procedure:
      currentCountingIndex?.counting_procedure || filteredProceduresCON,
    counting_equipment: currentCountingIndex?.equipment || undefined,
    counting_date: formatDateOrNow(currentCountingIndex?.counting_date),
    counting_unit_types: getDefaultUnitType(),
    noise_back_1mc: currentCountingIndex?.noise_back_1mc
      ? OPTIONS_YES_NO.find(
          (option) =>
            option.value === Number(currentCountingIndex?.noise_back_1mc)
        )
      : undefined,

    //calculation
    laboratory: currentCalculationIndex?.laboratory || DEFAULT_LABORATORY,
    calculation_equipment: currentCalculationIndex?.equipment
      ? Array.isArray(currentCalculationIndex?.equipment)
        ? currentCalculationIndex?.equipment
        : consultEquipment(Number(currentCalculationIndex?.equipment))
      : currentCountingIndex?.equipment,
    calculation_analyst: currentCalculationIndex?.analyst || user,
    calculation_date: formatDateOrNow(
      currentCalculationIndex?.calculation_date
    ),
    start_date_time: formatDateOrNow(currentSampling?.date_start_sampling),
    end_date_time: formatDateOrNow(currentSampling?.date_end_sampling),
    calculation_delete_date: formatDateOrNow(
      currentPreparationIndex?.start_date
    ),
    calculation_read_date: formatDateOrNow(currentCountingIndex?.counting_date),
    calculation_amount_sample:
      currentPreparationIndex?.amount_sample || undefined,
    calculation_units: currentCountingIndex?.unit_types || undefined,
    calculation_tsie_sample: dryingProcessing?.wet_dry,
    calculation_shared: currentSample?.shared ? "Si" : "No",
    calculation_counting_date: currentCountingIndex?.counting_date || undefined,
    calculation_procedure:
      currentCalculationIndex?.calculation_procedure || filteredCalculationCAL,
    exposition_days: currentCalculationIndex?.exposition_days || undefined,
    transit_days: currentCalculationIndex?.transit_days || undefined,
    error_lecturas_usv:
      currentCalculationIndex?.error_lecturas_usv || undefined,
    valor_media_lecturas_usv:
      currentCalculationIndex?.valor_media_lecturas_usv || undefined,
    lectura_4_usv: currentCalculationIndex?.lectura_4_usv || null,
    lectura_3_usv: currentCalculationIndex?.lectura_3_usv || null,
    lectura_2_usv: currentCalculationIndex?.lectura_2_usv || null,
    lectura_1_usv: currentCalculationIndex?.lectura_1_usv || null,
    dosimetro: currentCalculationIndex?.dosimetro || undefined,
    group: currentCalculationIndex?.group
      ? OPTIONS_A_B.find(
          (option) => option.value === `${currentCalculationIndex?.group}`
        )
      : undefined,
    is_read:
      currentCalculationIndex?.is_read !== null &&
      currentCalculationIndex?.is_read !== undefined
        ? currentCalculationIndex?.is_read === "0"
          ? OPTIONS_YES_NO[1]
          : OPTIONS_YES_NO[0]
        : OPTIONS_YES_NO[0],
    is_delete:
      currentCalculationIndex?.is_delete !== null &&
      currentCalculationIndex?.is_delete !== undefined
        ? currentCalculationIndex?.is_delete === "0"
          ? OPTIONS_YES_NO[1]
          : OPTIONS_YES_NO[0]
        : OPTIONS_YES_NO[0],
    idResultPreload: currentCalculationIndex?.result?.id || undefined,
  };
};

const DPForm = () => {
  const { tabValueAnalysis } = useTabs();

  const form = useFormContext();
  const { idMuestra } = useParams();
  const { stateSample } = useSample();
  const { sampling } = stateSample;
  const { user } = useAuth();
  const filteredProceduresPRE = useFilteredProcedures("PRE");
  const filteredProceduresCON = useFilteredProcedures("CON");
  const filteredCalculationCAL = useFilteredProcedures("CAL");

  const {
    currentPreparation,
    preparationsList,
    currentCounting,
    currentCalculation,
  } = useAnalysis();

  const currentPreparationObject =
    preparationsList[currentPreparation.index as number];
  useEffect(() => {
    if (currentPreparationObject) {
      const currentValues = form.getValues();

      const newValues = {
        ...currentValues,
        ...getDefaultValues(
          currentPreparationObject,
          currentCounting,
          currentCalculation,
          sampling,
          user,
          filteredProceduresPRE?.[0],
          filteredProceduresCON?.[0],
          filteredCalculationCAL?.[0],
          idMuestra
        ),
      };

      form.reset(newValues);
    }
  }, [idMuestra, JSON.stringify(currentPreparationObject), currentCounting]);

  const CurrentComponent = setsAnalysisPhasesDP[tabValueAnalysis]?.component;
  return CurrentComponent ? <CurrentComponent /> : null;
};

export default DPForm;
